"use client";

import { type RouteErrorContext, useRouteErrorLog } from "@workspace/telemetry/route-error";
/**
 * Standalone UI for `app/global-error.tsx`.
 *
 * Next.js requires global-error to render its own `<html>`/`<body>` when the root
 * layout fails, so this page cannot use the normal app layout, `globals.css`, or
 * Tailwind. Inline CSS + `GlobalErrorBrandLogo` keep branding without a CSS bundle.
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error#global-error
 */
import { GlobalErrorBrandLogo } from "@workspace/ui/components/platform-app-logo";
import type { ReactNode } from "react";

type GlobalHttpErrorPageProps = {
  error: Error & { digest?: string };
  reset: () => void;
  logContext?: RouteErrorContext;
  /** Override the default 514 mark; must not rely on Tailwind. */
  logo?: ReactNode;
};

const globalErrorStyles = `
  :root {
    color-scheme: light dark;
    --ge-background: #ffffff;
    --ge-foreground: #090b0c;
    --ge-muted: #67787c;
    --ge-border: #e3e7e8;
    --ge-primary: #161b1d;
    --ge-primary-fg: #f9fbfb;
    --ge-card: #ffffff;
  }
  @media (prefers-color-scheme: dark) {
    :root {
      --ge-background: #000000;
      --ge-foreground: #f9fbfb;
      --ge-muted: #9ca8ab;
      --ge-border: rgb(255 255 255 / 10%);
      --ge-primary: #e3e7e8;
      --ge-primary-fg: #161b1d;
      --ge-card: #161b1d;
    }
  }
  .global-error-body {
    margin: 0;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--ge-background);
    color: var(--ge-foreground);
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .global-error-panel {
    max-width: 28rem;
    width: 100%;
    margin: 1.5rem;
    padding: 1.75rem 1.5rem;
    border-radius: 0.75rem;
    border: 1px solid var(--ge-border);
    background: var(--ge-card);
    text-align: center;
  }
  .global-error-logo-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 0.75rem;
  }
  .global-error-logo {
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    color: var(--ge-foreground);
  }
  .global-error-code {
    margin: 0 0 0.5rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 4.5rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.05em;
    color: color-mix(in srgb, var(--ge-muted) 35%, transparent);
  }
  .global-error-title {
    margin: 0 0 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
  }
  .global-error-desc {
    margin: 0 0 1.25rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--ge-muted);
  }
  .global-error-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  .global-error-btn {
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
  }
  .global-error-btn-primary {
    border: none;
    background: var(--ge-primary);
    color: var(--ge-primary-fg);
  }
  .global-error-btn-outline {
    border: 1px solid var(--ge-border);
    background: transparent;
    color: var(--ge-foreground);
  }
`;

export function GlobalHttpErrorPage({ error, reset, logContext, logo }: GlobalHttpErrorPageProps) {
  const titleId = "global-error-title";
  const descriptionId = "global-error-description";

  useRouteErrorLog(error, { app: logContext?.app, boundary: logContext?.boundary ?? "global" });

  return (
    <html lang="en">
      <head>
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <style dangerouslySetInnerHTML={{ __html: globalErrorStyles }} />
      </head>
      <body className="global-error-body">
        <div
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          className="global-error-panel"
          role="alert"
        >
          <p aria-hidden="true" className="global-error-code">
            500
          </p>
          <div className="global-error-logo-wrap">{logo ?? <GlobalErrorBrandLogo />}</div>
          <h1 className="global-error-title" id={titleId}>
            Something went wrong
          </h1>
          <p className="global-error-desc" id={descriptionId}>
            An unexpected error occurred. Try again or return home.
          </p>
          <div className="global-error-actions">
            <button
              className="global-error-btn global-error-btn-primary"
              type="button"
              onClick={() => reset()}
            >
              Try again
            </button>
            <a className="global-error-btn global-error-btn-outline" href="/">
              Go home
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
